import { useContext, useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { Form, Formik } from 'formik';

import { Edit } from '@/icons';
import { Button, Select } from '@/molecules';

import {
  CustomTag as CustomTagContext,
  CustomTagDefault,
} from '@/components/providers/CustomTag';
import { useAddToCart } from '@/lib/hooks';
import { ProductForm } from './ProductForm';
import { ProductFormMobile } from './ProductFormMobile';
import { QuantitySchema } from './utils';

import type { TypeCustomTagState } from '@/components/providers/CustomTag';
import type { FormikProps, FormikValues } from 'formik';
import type { MouseEvent } from 'react';
import type { TypeProductDescriptionProps } from '../product-description';
import type { ProductFormLayout } from './types';

const CustomTagModal = dynamic(() =>
  import('@/organisms').then((mod) => mod.CustomTagModal),
);

const getVariations = (variations: any) => {
  /* FIND ALL VARIATIONS WITH THIS OPTION */
  const optionsSelect: Array<any> = [];
  variations &&
    variations.edges.forEach((variation: any) => {
      variation.node.attributes.nodes.forEach((attribute: any) => {
        optionsSelect.push({
          value: attribute.value,
          label: attribute.value,
        });
      });
    });

  return optionsSelect;
};

const generateCustomTagMetadata = (customTag: TypeCustomTagState) => [
  { etiqueta: customTag.etiqueta },
  { etiquetalabel: customTag.etiquetalabel },
  { etiquetaimage: customTag.etiquetaimage },
  { etiquetacheckoutimage: customTag.etiquetacheckoutimage },
];

interface TypeCustomTagForm
  extends Pick<TypeProductDescriptionProps, 'variations'>,
    ProductFormLayout {}

export const CustomTagForm = ({
  moreInfo,
  variations,
  stock,
  productId,
  stockAlert,
  isSellerClosed,
}: TypeCustomTagForm) => {
  const mobile = useRef<FormikProps<FormikValues>>(null);
  const desktop = useRef<FormikProps<FormikValues>>(null);
  const [variation, setVariation] = useState<any>(false);
  const { customTag } = useContext(CustomTagContext);
  const [isShowCustomTag, setShowCustomTag] = useState(false);
  const [fValid, setFvalid] = useState(false);
  const { state, add } = useAddToCart();
  const formik = {
    onSubmit: (data: FormikValues) => {
      const pid = variations?.edges?.find(
        (vari: any) => vari.node.attributes.nodes[0].value === data.cantidad,
      )?.node.databaseId;

      if (pid)
        add({
          productId: pid,
          quantity: 1,
          extraData: generateCustomTagMetadata(customTag),
          gtm: { listName: 'product' },
        });
    },
    initialValues: { cantidad: '-' },
    validationSchema: QuantitySchema,
  };
  const productForm = {
    productId,
    isSellerClosed,
    state,
    moreInfo: (
      <>
        {variation ? null : <span>6 botellas desde</span>}
        {moreInfo}
      </>
    ),
    stock,
    stockAlert,
    price:
      fValid && variation
        ? variation.node.price
        : variations?.edges && variations.edges.length > 0
          ? variations.edges[0]?.node?.price
          : 0,
    regularPrice:
      fValid && variation
        ? variation.node.regularPrice
        : variations?.edges && variations.edges.length > 0
          ? variations.edges[0]?.node?.regularPrice
          : 0,
  };
  const onChange = (value: MouseEvent) => {
    const variation = variations?.edges.find(
      (vari: any) => vari.node.attributes.nodes[0].value === value,
    );

    setVariation(variation);
    setFvalid(true);
  };

  useEffect(() => {
    if (mobile.current) mobile.current.setFieldValue('cantidad', '-');
    if (desktop.current) desktop.current.setFieldValue('cantidad', '-');
  }, [productId]);

  return (
    <>
      <CustomTagModal open={isShowCustomTag} setOpen={setShowCustomTag} />

      <div className="mb-4 flex flex-col items-center justify-between gap-2">
        <Button
          variant="secondary"
          size="large-full"
          type="button"
          className="!h-[46px] !rounded-[60px]"
          onClick={(e: MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            setShowCustomTag(true);
          }}
          testid="edit"
        >
          {customTag.etiqueta !== '' ? 'Editar' : 'Diseña tu etiqueta'}
          <Edit width={16} height={16} />
        </Button>

        <p className="u-body u-body--xs hidden text-typo-secondary lg:block">
          Las etiquetas personalizadas de Estrella Galicia son vinilos adhesivos
          de alta calidad. Se pueden mojar y son anti-arañazos.
        </p>
      </div>

      <Formik innerRef={desktop} validateOnMount validateOnChange {...formik}>
        {({ isValid }) => (
          <Form className="relative flex flex-col" noValidate>
            <ProductForm
              disabled={!isValid || CustomTagDefault.customTag === customTag}
              quantitySelector={
                <Select
                  name="cantidad"
                  options={getVariations(variations)}
                  placeholder="-"
                  id="cantidad"
                  theme="product"
                  onChange={onChange}
                />
              }
              {...productForm}
            />
          </Form>
        )}
      </Formik>

      <ProductFormMobile
        quantitySelector={
          <Select
            name="cantidad"
            options={getVariations(variations)}
            placeholder="Cantidad: -"
            id="cantidad"
            theme="tertiary"
            onChange={onChange}
            classNameSelect="!h-full !text-base"
            reverse
          />
        }
        {...productForm}
      >
        {(common) => (
          <Formik innerRef={mobile} {...formik}>
            <Form
              className="fixed bottom-0 left-0 right-0 z-20 grid h-14 grid-cols-[repeat(auto-fit,_minmax(0,_1fr))] lg:hidden"
              noValidate
            >
              {common({
                disabled:
                  CustomTagDefault.customTag === customTag || !variation,
              })}
            </Form>
          </Formik>
        )}
      </ProductFormMobile>
    </>
  );
};
